import { useEffect } from "react";
import svgPaths from "../../assets/new_ui_imports/InvestorRelations/svg-p56ic0xdxn.js";
import imgHeroBg from "../../assets/new_ui_img/investorrel_banner.jpg";
import imgParkingMap from "../../assets/new_ui_img/market_opportunity.png";
import imgBusmodel from "../../assets/new_ui_img/business_model.png";
import imgQuotes from "../../assets/new_ui_img/Quotes.svg";
import { Header, Footer, ScrollToTop } from "../landingPageNewUI/components/NewHeader";

// --- Scroll reveal ------------------------------------------------------------
function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.07, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".co-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// --- Hero ---------------------------------------------------------------------
function HeroSection() {
  return (
    <div className="pt-20">
      <div className="relative w-full overflow-hidden" style={{ height: "140px" }}>
        {/* Background image */}
        <div className="absolute inset-0 hero-bg-animate" style={{ transformOrigin: "center top" }}>
          <img alt="" className="absolute inset-0 max-w-none pointer-events-none size-full" src={imgHeroBg} />
        </div>
        {/* White overlay */}
        <div className="absolute inset-0 bg-[rgba(255,255,255,0.8)]" />
        {/* Title */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 h-full flex items-center">
          <p className="hero-title-animate [word-break:break-word] font-['Montserrat:Bold',sans-serif] font-bold leading-[1.3] text-[clamp(1.4rem,3.2vw,2.75rem)] text-black">
            Investor Relations
          </p>
        </div>
      </div>
    </div>
  );
}

// --- Section 1: Building the Infrastructure Layer ----------------------------
function InfrastructureSection() {
  return (
    <section className="bg-white py-[clamp(40px,5vw,60px)]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="co-reveal flex flex-col gap-[clamp(24px,3vw,40px)]">
          <p className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium text-black text-[clamp(1.5rem,2.5vw,2.5rem)] leading-[1.3]"
            style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
            Building the Infrastructure Layer for Autonomous Mobility
          </p>
          <div className="flex flex-col gap-[24px] font-['Lato:Regular',sans-serif] text-[#6b6b6b] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3]">
            <p>GeoParking Technologies enables autonomous vehicle fleets to access underutilized parking assets through a scalable platform connecting property owners with the future of transportation.</p>
            <p>As autonomous mobility expands, GeoParking is building the network infrastructure needed to support fleet operations, optimize parking utilization, and help cities adapt to a changing transportation landscape.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Section 2: Market Opportunity -------------------------------------------
function MarketOpportunitySection() {
  return (
    <section className="bg-white py-[clamp(40px,5vw,60px)]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="co-reveal flex flex-col lg:flex-row items-center gap-[clamp(32px,5vw,60px)]">
          {/* Text */}
          <div className="flex flex-[1_0_0] flex-col gap-[clamp(24px,3vw,40px)] min-w-0">
            <p className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium text-black text-[clamp(1.5rem,2.5vw,2.5rem)] leading-[1.3]"
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              Market Opportunity
            </p>
            <div className="flex flex-col gap-[24px] font-['Lato:Regular',sans-serif] text-[#6b6b6b] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3]">
              <p>The transition to autonomous mobility represents a significant shift in transportation infrastructure.</p>
              <p>GeoParking is positioned at the intersection of autonomous vehicles, real estate utilization, and smart city development.</p>
            </div>
          </div>
          {/* Illustration */}
          <div className="w-full lg:w-[55%] flex-shrink-0 rounded-[10px] overflow-hidden">
            <img
              alt="Parking concept map illustration"
              className="w-full h-auto object-contain"
              src={imgParkingMap}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Section 3: Business Model ------------------------------------------------
function BusinessModelSection() {
  return (
    <section className="bg-white py-[clamp(40px,5vw,60px)]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="co-reveal flex flex-col lg:flex-row items-center gap-[clamp(32px,5vw,60px)]">
          {/* Illustration */}
          <div className="w-full lg:w-[55%] flex-shrink-0 rounded-[10px] overflow-hidden">
            <img
              alt="Parking concept map illustration"
              className="w-full h-auto object-contain"
              src={imgBusmodel}
            />
          </div>
          {/* Text */}
          <div className="flex flex-[1_0_0] flex-col gap-[clamp(24px,3vw,40px)] min-w-0">
            <p className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium text-black text-[clamp(1.5rem,2.5vw,2.5rem)] leading-[1.3]"
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              Market Opportunity
            </p>
            <p className="font-['Lato:Regular',sans-serif] text-[#6b6b6b] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3]">
              GeoParking creates value by connecting autonomous fleets with underutilized parking assets:
            </p>
            <ul className="font-['Lato:Regular',sans-serif] text-[#6b6b6b] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3] list-disc space-y-[16px]">
              <li className="ms-[30px]">Property owners monetize unused parking capacity.</li>
              <li className="ms-[30px]">Autonomous fleets gain access to strategically located parking and operational spaces.</li>
              <li className="ms-[30px]">GeoParking provides the marketplace and technology infrastructure that enables these connections.</li>
            </ul>            
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Section 4: Dark investor section ----------------------------------------
function IconDart() {
  return (
    <div className="flex-shrink-0 size-[clamp(48px,5vw,72px)]">
      <svg fill="none" className="size-full" viewBox="0 0 72 72">
        <path d={svgPaths.p2e657000} stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="4.25" />
      </svg>
    </div>
  );
}

function IconConnect() {
  return (
    <div className="flex-shrink-0 size-[clamp(48px,5vw,72px)]">
      <svg fill="none" className="size-full" viewBox="0 0 72 72">
        <path d={svgPaths.p39a4e680} fill="#43D651" />
      </svg>
    </div>
  );
}

function TechChip({ icon, label }) {
  return (
    <div className="bg-white flex gap-[8px] items-center justify-center px-[20px] py-[13px] rounded-[5px]">
      {icon}
      <p className="font-['Lato:Regular',sans-serif] text-[#121212] text-[clamp(0.85rem,1vw,1rem)] leading-[1.3] whitespace-nowrap">{label}</p>
    </div>
  );
}

function IconCar() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_9625_85322)">
        <path d="M15.8984 11.7969C14.9292 11.7969 14.1406 12.5854 14.1406 13.5547C14.1406 14.5239 14.9292 15.3125 15.8984 15.3125C16.8677 15.3125 17.6562 14.5239 17.6562 13.5547C17.6562 12.5854 16.8677 11.7969 15.8984 11.7969ZM15.8984 14.1406C15.5754 14.1406 15.3125 13.8778 15.3125 13.5547C15.3125 13.2316 15.5754 12.9688 15.8984 12.9688C16.2215 12.9688 16.4844 13.2316 16.4844 13.5547C16.4844 13.8778 16.2215 14.1406 15.8984 14.1406Z" fill="black"/>
        <path d="M4.10156 11.7969C3.1323 11.7969 2.34375 12.5854 2.34375 13.5547C2.34375 14.5239 3.1323 15.3125 4.10156 15.3125C5.07082 15.3125 5.85938 14.5239 5.85938 13.5547C5.85938 12.5854 5.07082 11.7969 4.10156 11.7969ZM4.10156 14.1406C3.77848 14.1406 3.51562 13.8778 3.51562 13.5547C3.51562 13.2316 3.77848 12.9688 4.10156 12.9688C4.42465 12.9688 4.6875 13.2316 4.6875 13.5547C4.6875 13.8778 4.42465 14.1406 4.10156 14.1406Z" fill="black"/>
        <path d="M11.1719 3.51562H10.5859V2.92969C10.5859 2.60609 10.3236 2.34375 10 2.34375C9.67641 2.34375 9.41406 2.60609 9.41406 2.92969V4.10156C9.41406 4.42516 9.67641 4.6875 10 4.6875H11.1719C11.4955 4.6875 11.7578 4.42516 11.7578 4.10156C11.7578 3.77797 11.4955 3.51562 11.1719 3.51562Z" fill="black"/>
        <path d="M17.54 9.49125L16.9107 6.90664C16.5916 5.63035 15.3954 4.70191 14.0595 4.68816C14.087 4.49648 14.1016 4.3007 14.1016 4.10156C14.1016 1.83996 12.2616 0 10 0C7.7384 0 5.89844 1.83996 5.89844 4.10156C5.89844 4.3007 5.91301 4.49648 5.94055 4.68816C4.60457 4.70191 3.40832 5.63039 3.08844 6.91016L2.46 9.49129C1.06715 9.71676 0 10.9273 0 12.3828V14.7266C0 16.1414 1.00812 17.3251 2.34375 17.5973V18.2422C2.34375 19.2114 3.1323 20 4.10156 20H5.27344C6.2448 20 7.07031 19.215 7.07031 18.2422V17.6562H12.9297V18.2422C12.9297 19.2138 13.7537 20 14.7266 20H15.8984C16.8677 20 17.6562 19.2114 17.6562 18.2422V17.5973C18.9919 17.3251 20 16.1414 20 14.7266V12.3828C20 10.9273 18.9329 9.71676 17.54 9.49125ZM10 1.17188C11.6154 1.17188 12.9297 2.48613 12.9297 4.10156C12.9297 5.71699 11.6154 7.03125 10 7.03125C8.38457 7.03125 7.07031 5.71699 7.07031 4.10156C7.07031 2.48613 8.38457 1.17188 10 1.17188ZM4.22617 7.19082C4.41906 6.41934 5.15266 5.85938 5.97055 5.85938H6.29469C6.95418 7.24387 8.36707 8.20312 9.99996 8.20312C11.6329 8.20312 13.0458 7.24387 13.7052 5.85938H14.0294C14.8473 5.85938 15.5809 6.41934 15.7729 7.18734L16.3246 9.45312H3.67539L4.22617 7.19082ZM5.89844 18.2422C5.89844 18.5543 5.60637 18.8281 5.27344 18.8281H4.10156C3.77848 18.8281 3.51562 18.5653 3.51562 18.2422V17.6562H5.89844V18.2422ZM7.43246 16.4844L8.0184 15.3125H11.9816L12.5676 16.4844H7.43246ZM16.4844 18.2422C16.4844 18.5653 16.2215 18.8281 15.8984 18.8281H14.7266C14.3936 18.8281 14.1016 18.5543 14.1016 18.2422V17.6562H16.4844V18.2422ZM18.8281 14.7266C18.8281 15.6958 18.0396 16.4844 17.0703 16.4844H13.8778L12.8679 14.4645C12.7686 14.266 12.5657 14.1406 12.3438 14.1406H7.65625C7.4343 14.1406 7.23144 14.266 7.13215 14.4645L6.12223 16.4844H2.92969C1.96043 16.4844 1.17188 15.6958 1.17188 14.7266V12.3828C1.17188 11.4143 1.95926 10.6263 2.9275 10.625C2.9284 10.625 2.92926 10.6251 2.93016 10.6251C2.93121 10.6251 2.93227 10.625 2.93332 10.625H17.0667C17.0677 10.625 17.0688 10.6251 17.0698 10.6251C17.0704 10.6251 17.071 10.625 17.0716 10.625C18.0402 10.6257 18.8281 11.414 18.8281 12.3828V14.7266Z" fill="black"/>
        <path d="M12.3438 11.7969H7.65625C7.33266 11.7969 7.07031 12.0592 7.07031 12.3828C7.07031 12.7064 7.33266 12.9688 7.65625 12.9688H12.3438C12.6673 12.9688 12.9297 12.7064 12.9297 12.3828C12.9297 12.0592 12.6673 11.7969 12.3438 11.7969Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_9625_85322">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
}

function IconApi() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_9625_85340)">
        <path d="M7.125 9.72253V3.625C7.125 2.17525 8.30026 1 9.75 1C11.1997 1 12.375 2.17525 12.375 3.625V4.50249M12.375 9.75153V15.875C12.375 17.3247 11.1997 18.5 9.75 18.5C8.30026 18.5 7.125 17.3247 7.125 15.875V14.9869M9.75 12.375H3.61807C2.17215 12.375 1 11.1997 1 9.75C1 8.30026 2.17215 7.125 3.61807 7.125H4.49501M9.75 7.125H15.8701C17.3226 7.125 18.5 8.30026 18.5 9.75C18.5 11.1997 17.3226 12.375 15.8701 12.375H15.029" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
      </g>
      <defs>
        <clipPath id="clip0_9625_85340">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
}

function IconScale() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_9625_85344)">
        <path d="M19.1526 6.88895L11.0584 1.4859C10.4154 1.0568 9.58452 1.05676 8.94155 1.48594L0.847406 6.88895C-0.0268912 7.47258 -0.263376 8.65871 0.320218 9.53305C0.707406 10.113 1.35987 10.412 2.01049 10.3773V18.232C2.01049 18.5639 2.27955 18.8329 2.61143 18.8329H17.3885C17.7204 18.8329 17.9895 18.5639 17.9895 18.232V10.378C18.0239 10.3799 18.0583 10.3812 18.093 10.3812H18.093C18.7321 10.3811 19.3253 10.0641 19.6797 9.53301C20.2634 8.65867 20.0269 7.47258 19.1526 6.88895ZM16.7876 17.631H3.21233V9.89457L9.99998 5.36371L16.7876 9.89457V17.631H16.7876ZM18.6801 8.8657C18.5491 9.06199 18.3296 9.17922 18.0929 9.17926C17.9541 9.17926 17.8193 9.13824 17.703 9.06055L10.3337 4.14137C10.2327 4.07395 10.1163 4.04023 10 4.04023C9.88373 4.04023 9.76737 4.07398 9.66639 4.14137L2.29705 9.06059C1.97385 9.27629 1.53553 9.18887 1.31983 8.86578C1.10412 8.5427 1.19155 8.1043 1.51467 7.88855L9.60877 2.48559C9.84647 2.32703 10.1536 2.32699 10.3912 2.48555L18.4853 7.88852C18.8084 8.10422 18.8958 8.54262 18.6801 8.8657Z" fill="black"/>
        <path d="M13.5041 14.9677H7.10035V14.2297L9.10262 12.5202L9.40898 13.3123C9.48231 13.5018 9.64645 13.6415 9.84527 13.6835C10.0441 13.7255 10.2507 13.6642 10.3944 13.5205L12.6126 11.3023L12.571 11.5584C12.5177 11.8861 12.7402 12.1947 13.0677 12.248C13.1004 12.2533 13.1328 12.2558 13.1648 12.2558C13.4543 12.2558 13.7093 12.0461 13.7573 11.7512L14.0973 9.65832C14.1283 9.46758 14.0657 9.27359 13.9291 9.13699C13.7925 9.00039 13.5986 8.93762 13.4078 8.96879L11.3148 9.30883C10.9873 9.36203 10.7648 9.67074 10.8181 9.99832C10.8713 10.3259 11.18 10.5485 11.5076 10.4951L11.7613 10.4539L10.197 12.0182L9.9177 11.2959C9.84742 11.1141 9.69336 10.9778 9.50445 10.93C9.31555 10.8824 9.1152 10.9291 8.96703 11.0556L7.10031 12.6495V9.78121C7.10031 9.44934 6.83125 9.18027 6.49938 9.18027C6.1675 9.18027 5.89844 9.44934 5.89844 9.78121V15.5686C5.89844 15.9005 6.1675 16.1695 6.49938 16.1695H13.5041C13.836 16.1695 14.1051 15.9005 14.1051 15.5686C14.1051 15.2367 13.8361 14.9677 13.5041 14.9677Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_9625_85344">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
}

function InvestorSection() {
  return (
    <section className="bg-[#151515] py-[clamp(60px,7vw,103px)] relative overflow-hidden">
      <div className="absolute inset-0 hero-bg-animate w-[276px] h-[276px] bottom-0 right-0 pointer-events-none" style={{ transformOrigin: "right bottom" }}>
        <img alt="" className="absolute inset-0 pointer-events-none w-[276px] h-[276px]" src={imgQuotes} />
      </div>
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col gap-[clamp(40px,5vw,60px)]">

          {/* Heading */}
          <div className="co-reveal flex flex-col gap-[12px]">
            <p className="font-['Montserrat:Medium',sans-serif] font-medium text-white text-[clamp(1.5rem,2.5vw,2.5rem)] leading-[1.3]">
              The Digital Infrastructure Behind Robotaxi Fleets
            </p>
            <p className="font-['Lato:Regular',sans-serif] text-white text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3] max-w-[1400px]">
              GeoParking is accelerating the shift toward sustainable mobility by transforming existing parking infrastructure into a connected network for autonomous electric vehicle fleets. We create new revenue opportunities for property owners while enabling the next generation of clean transportation.
            </p>
          </div>

          {/* Mission + How We Do It */}
          <div className="co-reveal flex flex-col sm:flex-row gap-[clamp(32px,4vw,40px)]">
            {/* Our Mission */}
            <div className="flex gap-[24px] items-start w-full sm:w-auto flex-1">
              <IconDart />
              <div className="flex flex-col gap-[12px]">
                <p className="font-['Montserrat:Bold',sans-serif] font-bold text-white text-[clamp(1.2rem,1.8vw,2rem)] leading-[1.3]">Our Mission</p>
                <p className="font-['Lato:Regular',sans-serif] text-white text-[clamp(1rem,1.1vw,1.25rem)] leading-[1.3]">
                  Our mission is to unlock underutilized parking assets, enable the growth of autonomous electric fleets, and help build cleaner, smarter cities for the future.
                </p>
              </div>
            </div>

            {/* How We Do It */}
            <div className="flex gap-[24px] items-start w-full sm:w-auto flex-1">
              <IconConnect />
              <div className="flex flex-col gap-[12px]">
                <p className="font-['Montserrat:Bold',sans-serif] font-bold text-white text-[clamp(1.2rem,1.8vw,2rem)] leading-[1.3]">How We Do It</p>
                <p className="font-['Lato:Regular',sans-serif] text-white text-[clamp(1rem,1.1vw,1.25rem)] leading-[1.3]">
                  GeoParking's software connects fleet operators with available parking while giving property owners complete control over when and where their spaces are available. Our platform creates a scalable parking network that supports autonomous mobility while generating new revenue for property owners.
                </p>
              </div>
            </div>
          </div>

          {/* Technology chips */}
          <div className="co-reveal flex flex-wrap gap-[18px] center justify-center">
            <TechChip icon={<IconCar />} label="Real-Time Fleet Integration" />
            <TechChip icon={<IconApi />} label="Fleet API & Digital Middleware" />
            <TechChip icon={<IconScale />} label="Scalable for Any Property or Fleet" />
          </div>

          {/* CTA */}
          <div className="co-reveal flex justify-center">
            <div className="border border-white rounded-[5px] px-[20px] py-[12px] inline-flex items-center gap-[16px]">
              <p className="font-['Lato:Regular',sans-serif] text-white text-[clamp(1rem,1.1vw,1.125rem)] text-center leading-normal">
                Ready to see how much your parking could earn?
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// --- Company Page -------------------------------------------------------------
export default function InvestorRelationsPage() {
  useReveal();

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <InfrastructureSection />
        <MarketOpportunitySection />
        <BusinessModelSection />
        <InvestorSection />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
